import React, { useEffect, useState } from "react";
import PublicLayout from "@/components/PublicLayout";
import { api } from "@/lib/api";
import { formatCAD } from "@/lib/utils-cart";
import { Star, Briefcase, Calculator, Megaphone, Shield, ShieldCheck, Server, Cog, Users } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const CATEGORY_ICONS = {
  accounting: Calculator,
  payroll: Users,
  marketing: Megaphone,
  legal: ShieldCheck,
  insurance: Shield,
  cybersecurity: ShieldCheck,
  it_services: Server,
  erp_consulting: Cog,
  hr_services: Users,
};

export default function Services() {
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [category, setCategory] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [leadForm, setLeadForm] = useState({ category: "accounting", description: "", budget: "", timeline: "" });

  useEffect(() => {
    const q = category ? `?category=${category}` : "";
    api.get(`/services${q}`).then((r) => setServices(r.data)).catch(() => {});
  }, [category]);

  const submitLead = async (e) => {
    e.preventDefault();
    if (!user) return toast.error("Sign in to request services");
    try {
      await api.post("/leads", { ...leadForm, budget: leadForm.budget ? Number(leadForm.budget) : undefined });
      toast.success("Lead posted — providers will respond shortly");
      setRequesting(false);
      setLeadForm({ category: "accounting", description: "", budget: "", timeline: "" });
    } catch (e) {
      toast.error("Failed to submit");
    }
  };

  const cats = Object.keys(CATEGORY_ICONS);

  return (
    <PublicLayout>
      <section className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="label-eyebrow text-secondary mb-2">Business Services Marketplace</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-primary leading-none">Vetted Canadian providers for everything beyond commerce.</h1>
          <p className="mt-4 text-muted-foreground max-w-2xl">Accounting, payroll, legal, marketing, insurance, IT, cybersecurity, HR — paid through Vendora, with transparent quotes.</p>
          <div className="mt-8 flex flex-wrap gap-2">
            <button onClick={() => setCategory("")} className={`v-btn-outline ${!category ? "border-primary bg-primary text-white" : ""}`} data-testid="services-cat-all">All</button>
            {cats.map((c) => (
              <button key={c} onClick={() => setCategory(c)} className={`v-btn-outline ${category === c ? "border-primary bg-primary text-white" : ""}`} data-testid={`services-cat-${c}`}>
                {c.replace("_", " ")}
              </button>
            ))}
          </div>
          <div className="mt-6">
            <button onClick={() => setRequesting(!requesting)} className="v-btn-primary" data-testid="services-request-lead">
              <Briefcase size={16} /> Post a service request
            </button>
          </div>
        </div>
      </section>

      {requesting && (
        <section className="border-b border-border bg-muted/40">
          <div className="max-w-3xl mx-auto px-6 py-8">
            <form onSubmit={submitLead} className="v-card">
              <h2 className="font-display text-xl font-bold text-primary">Tell providers what you need</h2>
              <div className="mt-4 grid gap-3">
                <div>
                  <label className="label-eyebrow mb-2 block">Category</label>
                  <select value={leadForm.category} onChange={(e) => setLeadForm({...leadForm, category: e.target.value})} className="v-input" data-testid="lead-category">
                    {cats.map((c) => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-eyebrow mb-2 block">Describe what you need</label>
                  <textarea rows={3} value={leadForm.description} onChange={(e) => setLeadForm({...leadForm, description: e.target.value})} required className="v-input" data-testid="lead-description" />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="label-eyebrow mb-2 block">Budget (CAD, optional)</label>
                    <input type="number" value={leadForm.budget} onChange={(e) => setLeadForm({...leadForm, budget: e.target.value})} className="v-input" data-testid="lead-budget" />
                  </div>
                  <div>
                    <label className="label-eyebrow mb-2 block">Timeline (optional)</label>
                    <input value={leadForm.timeline} onChange={(e) => setLeadForm({...leadForm, timeline: e.target.value})} placeholder="e.g. within 2 weeks" className="v-input" data-testid="lead-timeline" />
                  </div>
                </div>
                <div className="flex gap-2 mt-2">
                  <button className="v-btn-primary" data-testid="lead-submit">Post lead</button>
                  <button type="button" onClick={() => setRequesting(false)} className="v-btn-outline">Cancel</button>
                </div>
              </div>
            </form>
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-6 py-10 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {services.map((s) => {
          const Icon = CATEGORY_ICONS[s.category] || Briefcase;
          return (
            <div key={s.id} className="v-card flex flex-col" data-testid={`service-card-${s.id}`}>
              <Icon size={24} className="text-secondary" strokeWidth={1.75} />
              <div className="mt-4 label-eyebrow text-muted-foreground">{s.category.replace("_", " ")}</div>
              <h3 className="font-display text-lg font-semibold text-primary mt-1">{s.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground flex-1">{s.description}</p>
              <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-sm">
                <span className="font-mono font-semibold text-primary">{formatCAD(s.price_from)} / {s.pricing_unit}</span>
                <span className="flex items-center gap-1 text-amber-600 text-xs"><Star size={12} fill="currentColor" /> {s.rating} ({s.reviews})</span>
              </div>
              <div className="mt-1 text-xs text-muted-foreground font-mono">{s.provider_name}</div>
            </div>
          );
        })}
      </section>
    </PublicLayout>
  );
}
